import {noop} from '@benchling/js-utils';
import {createStyles, Group, SegmentedControl} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import ScratchPlateRecordsView, {
  buildScratchPlateRecordsItems,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateRecordsView';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {DisplayLayerOptionSection} from 'samples/plate-maps/PlateMapConstants';
import PlateMapSummary from 'samples/plate-maps/PlateMapSummary';
import {getReadonlyParameterLayerDisplayOptions} from 'samples/plate-maps/PlateMapUtil';
import {ScratchPlateDesignState} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import SaveScratchPlateAsDesignTemplateModal from './SaveScratchPlateAsDesignTemplateModal';
import {
  scratchPlateSchemaId,
  useScratchPlateCreatedPlates,
  useScratchPlateData,
  useWellSchemaOptionsForPlateMap,
} from './ScratchPlateDesignSummaryUtil';

export interface ScratchPlateDesignSummaryProps {
  scratchPlateId: string;
  locked: boolean;
  viewer: boolean;
  collapseId?: string;
  onEdit: (initialState: ScratchPlateDesignState, scratchPlateId: string, refetch: () => void) => void;
  onFillPlates?: (
    initialDesignState: ScratchPlateDesignState,
    originDesignApiId: string,
    refetch: () => void
  ) => void;
  shouldQueryCreatedPlates?: boolean;
}

const useStyles = createStyles((theme) => ({
  errorBannerWrapper: {
    alignSelf: 'flex-start',
  },
  viewToggle: {
    marginBottom: theme.spacing.xs,
  },
}));

enum PlateView {
  DESIGN = 'design',
  RECORDS = 'records',
}

export default function ScratchPlateDesignSummary({
  scratchPlateId,
  locked,
  viewer,
  onEdit,
  onFillPlates,
  collapseId,
  shouldQueryCreatedPlates = true,
}: ScratchPlateDesignSummaryProps): JSX.Element {
  const {classes} = useStyles();
  // `scratchPlateDesign` is the design object; the records list comes from a dedicated created-plates query.
  const {
    plate: scratchPlateDesign,
    plateMap,
    containerSchemaId,
    dataFrameIsInaccessible,
    refetch,
  } = useScratchPlateData(scratchPlateId);
  const {createdPlates, refetch: refetchCreatedPlates} = useScratchPlateCreatedPlates(
    scratchPlateId,
    shouldQueryCreatedPlates
  );
  const [saveAsDesignModalOpen, setSaveAsDesignModalOpen] = useState(false);
  const [activeView, setActiveView] = useState<PlateView>(PlateView.DESIGN);
  const [selectedPlateId, setSelectedPlateId] = useState<string | null>(null);

  const wellSchemaLayers = useWellSchemaLayers(containerSchemaId, noop, []);
  const wellSchemaOptions = useWellSchemaOptionsForPlateMap(plateMap, wellSchemaLayers);
  const plateDataParametersOptions = useMemo(
    () => (plateMap != null ? getReadonlyParameterLayerDisplayOptions(plateMap) : []),
    [plateMap]
  );

  const isDisabled =
    !BenchlingConfig.ENABLE_SCRATCH_PLATES || locked || scratchPlateDesign == null || plateMap == null;
  const recordsItems = useMemo(() => buildScratchPlateRecordsItems(createdPlates), [createdPlates]);
  const hasRecords = createdPlates.length > 0;
  function refetchAllData(): void {
    refetch();
    refetchCreatedPlates();
  }
  const effectiveView: PlateView = hasRecords ? activeView : PlateView.DESIGN;
  const firstPlateId = recordsItems.find((item) => item.kind === 'plate')?.id ?? null;
  const validSelectedPlateId =
    selectedPlateId != null &&
    recordsItems.some((item) => item.kind === 'plate' && item.id === selectedPlateId)
      ? selectedPlateId
      : firstPlateId;
  const viewToggle = hasRecords ? (
    <SegmentedControl
      aria-label="Plate view mode"
      className={classes.viewToggle}
      fullWidth={true}
      value={effectiveView}
      data={[
        {label: 'Design', value: PlateView.DESIGN},
        {label: `Plates (${createdPlates.length})`, value: PlateView.RECORDS},
      ]}
      onChange={(value) => {
        if (value === PlateView.DESIGN || value === PlateView.RECORDS) {
          setActiveView(value);
        }
      }}
    />
  ) : null;

  function buildDesignState(
    nonNullPlate: NonNullable<typeof scratchPlateDesign>,
    nonNullPlateMap: NonNullable<typeof plateMap>
  ): ScratchPlateDesignState {
    const {name, height, width} = nonNullPlate;
    const plateSchemaId = scratchPlateSchemaId(nonNullPlate);
    const plateSize: ScratchPlateDesignState['plateSize'] = [height, width];
    const plateSchema = plateSchemaId != null ? SampleSchemaStore.getPlateSchema(plateSchemaId) : null;
    return {name, plateSize, plateSchema, plateMap: nonNullPlateMap};
  }

  function handleEdit(): void {
    if (scratchPlateDesign == null || plateMap == null) {
      return;
    }
    onEdit(buildDesignState(scratchPlateDesign, plateMap), scratchPlateId, refetchAllData);
  }

  function handleFillPlates(): void {
    if (scratchPlateDesign == null || plateMap == null || onFillPlates == null) {
      return;
    }
    onFillPlates(buildDesignState(scratchPlateDesign, plateMap), scratchPlateId, refetchAllData);
  }

  const headerControls = !viewer ? (
    <Group spacing={8} noWrap={true}>
      <Button
        size={ButtonSize.MINI}
        icon="pen"
        aria-label="Edit plate design"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        disabled={isDisabled}
        onClick={handleEdit}
      />
      {BenchlingConfig.ENABLE_SCRATCH_PLATES && (
        <OverflowMenu
          enableTooltip={false}
          buttonProps={{'aria-label': 'More options', className: undefined, disabled: isDisabled}}
          menuProps={{dropLeft: true}}
          options={[
            {
              contents: <>Save as template</>,
              onSelect: () => {
                setSaveAsDesignModalOpen(true);
              },
              isDisabled: scratchPlateDesign == null,
            },
          ]}
        />
      )}
      {onFillPlates != null && (
        <Button
          size={ButtonSize.MINI}
          icon="fill-grid"
          text="Fill plates"
          buttonStyle={ButtonStyle.PRIMARY}
          disabled={isDisabled}
          onClick={handleFillPlates}
        />
      )}
    </Group>
  ) : null;

  return (
    <>
      <PlateMapContainer
        header="Plate design"
        headerControls={headerControls}
        subheader={scratchPlateDesign?.name ?? scratchPlateId}
        onHeaderContextMenu={noop}
        plateMapType={PlateMapType.DESIGN}
        collapseId={collapseId}
      >
        {dataFrameIsInaccessible ? (
          <div className={classes.errorBannerWrapper}>
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="You do not have access to this plate design."
            />
          </div>
        ) : effectiveView === PlateView.RECORDS ? (
          scratchPlateDesign != null ? (
            <ScratchPlateRecordsView
              recordsItems={recordsItems}
              selectedPlateId={validSelectedPlateId}
              onSelectPlateId={setSelectedPlateId}
              viewToggle={viewToggle}
              onFillAdditionalPlates={!viewer && onFillPlates != null ? handleFillPlates : undefined}
              fillAdditionalPlatesDisabled={isDisabled}
              designPlateSize={[scratchPlateDesign.height, scratchPlateDesign.width]}
              designPlateSchemaId={scratchPlateSchemaId(scratchPlateDesign)}
              onPutInInventoryComplete={refetchCreatedPlates}
            />
          ) : (
            <CenteredHorizontalThrobber />
          )
        ) : plateMap != null ? (
          <PlateMapSummary
            plateMap={plateMap}
            plateDataParametersOptions={plateDataParametersOptions}
            wellSchemaOptions={wellSchemaOptions}
            frozenReagentAssignmentGroups={null}
            procedureStudyDesignType={null}
            plateMapType={PlateMapType.DESIGN}
            hiddenSections={[DisplayLayerOptionSection.CONTENTS]}
            additionalMetadataContent={<></>}
            isDisplayingScratchPlate={true}
            stickyHeader={viewToggle}
          />
        ) : (
          <CenteredHorizontalThrobber />
        )}
      </PlateMapContainer>
      {saveAsDesignModalOpen && (
        <SaveScratchPlateAsDesignTemplateModal
          scratchPlateId={scratchPlateId}
          defaultName={scratchPlateDesign?.name ?? ''}
          opened={saveAsDesignModalOpen}
          onClose={() => {
            setSaveAsDesignModalOpen(false);
          }}
        />
      )}
    </>
  );
}
